import Keycloak from "./libs/keycloak.js";

// Route segments and id params (server id, reseller id) are identifiers — numeric or UUID — never
// path data. A value with other characters (e.g. a decoded path separator from "..%2Fusers%2F8204")
// must not reach the backend API path builder, or a crafted SCP UI link could escape the intended
// endpoint and issue authenticated requests elsewhere on the same origin. Reused by the route guard
// in router.js for in-app navigation.
const VALID_ROUTE_ID = /^[0-9a-zA-Z-]+$/;
export const isSafeRouteValue = (value) => VALID_ROUTE_ID.test(value);

const APP_BASE = (window.__env__?.SCP_UI_CONTEXT_PATH || '') + '/';

// Block client-side path traversal on initial load — before Keycloak init bakes the path into its
// redirect_uri. Every decoded path segment under the app base must be identifier-safe; otherwise
// send the URL to the start page.
const isSafePathname = (pathname) => !pathname.startsWith(APP_BASE)
    || pathname.slice(APP_BASE.length).split('/').filter(Boolean).every((segment) => {
        let decoded;
        try { decoded = decodeURIComponent(segment); } catch { decoded = segment; }
        return isSafeRouteValue(decoded);
    });

if (!isSafePathname(window.location.pathname)) {
    window.location.replace(APP_BASE);
}

const KEYCLOAK_BASE = window.__env__.SCP_UI_KEYCLOAK_URL.replace(/\/$/, '');

export const keycloak = new Keycloak({
    url: window.__env__.SCP_UI_KEYCLOAK_URL,
    realm: "scp",
    clientId: "scp"
});

// These exported bindings MUST be declared before the top-level `await` bootstrap
// below. In a module with top-level await, a `const`/`let` declared after an `await`
// stays in the temporal dead zone while the module is suspended. iOS Safari
// (JavaScriptCore) and bundled builds can then surface that to importers as
// "ReferenceError: Cannot access uninitialized variable" — e.g. http.getBinary()
// reaching ensureFreshToken during login — leaving the app stuck on the splash.
export const ensureFreshToken = () => keycloak.updateToken().catch(() => keycloak.login());

const APP_ORIGIN = window.location.origin;
const REAUTH_REDIRECT_URI = `${APP_ORIGIN}${APP_BASE}reauth-callback.html`;

const base64UrlEncode = (bytes) => btoa(String.fromCharCode(...new Uint8Array(bytes)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');

// Proof Key for Code Exchange
const createPkcePair = async () => {
    const verifier = base64UrlEncode(crypto.getRandomValues(new Uint8Array(64)));
    const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
    return { verifier, challenge: base64UrlEncode(digest) };
};

const decodeJwtSub = (jwt) => {
    try {
        const part = jwt.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
        const pad = part.length % 4 === 0 ? '' : '='.repeat(4 - (part.length % 4));
        return JSON.parse(atob(part + pad))?.sub;
    } catch {
        return undefined;
    }
};

const getTokenByAuthorizationCode = async (code, verifier) => {
    return await fetch(
        `${KEYCLOAK_BASE}/realms/scp/protocol/openid-connect/token`,
        {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: new URLSearchParams({
                grant_type: 'authorization_code',
                client_id: 'scp',
                code,
                redirect_uri: REAUTH_REDIRECT_URI,
                code_verifier: verifier,
            })
        }
    );
}

// The popup's interactive login (prompt=login) always establishes a fresh Keycloak SSO
// session in the browser, overwriting the cookie for the account that was already signed
// in — even when that login turns out to be the wrong user. Without this, a rejected
// step-up re-auth still leaves the browser's SSO cookie pointing at the other account, so a
// later page refresh (which re-runs keycloak.init()) silently signs the user in as them
// instead of re-prompting. mode: 'no-cors' is fine — we only need the cookie-clearing side
// effect of the request, not to read its response.
const endSsoSession = (idToken) => fetch(
    `${KEYCLOAK_BASE}/realms/scp/protocol/openid-connect/logout?${new URLSearchParams({
        id_token_hint: idToken,
        client_id: 'scp',
    })}`,
    { credentials: 'include', mode: 'no-cors' }
).catch(() => {});

// Step-up re-authentication for sensitive actions. Opens a Keycloak login popup that
// forces a fresh interactive login (prompt=login, max_age=0) via the Authorization Code
// + PKCE flow, and resolves to `true` only when the SAME user re-authenticates. Replaces
// the deprecated Resource Owner Password Credentials (grant_type=password) check, so the
// `scp` client no longer needs Direct Access Grants enabled.
//
// MUST be called synchronously from a user gesture (e.g. a click handler) — the popup is
// opened before the first `await`, otherwise browsers block it.
export const stepUpReauth = async () => {
    // Without a known current-user subject we cannot verify who
    // re-authenticated, so never proceed. (An authenticated session always has one.)
    const expectedSub = keycloak.tokenParsed?.sub;
    if (!expectedSub) return false;

    const popup = window.open('about:blank', 'scp-reauth', 'popup,width=480,height=640');
    if (!popup) return false;

    const loginHint = keycloak.tokenParsed?.preferred_username;
    const state = crypto.randomUUID();

    let pkce;
    try {
        pkce = await createPkcePair();
    } catch {
        popup.close();
        return false;
    }

    const params = new URLSearchParams({
        client_id: 'scp',
        redirect_uri: REAUTH_REDIRECT_URI,
        response_type: 'code',
        response_mode: 'query',
        scope: 'openid',
        prompt: 'login',
        max_age: '0',
        state,
        code_challenge: pkce.challenge,
        code_challenge_method: 'S256',
    });
    if (loginHint) params.set('login_hint', loginHint);
    popup.location.replace(`${KEYCLOAK_BASE}/realms/scp/protocol/openid-connect/auth?${params}`);

    const code = await new Promise((resolve) => {
        const poll = setInterval(() => { 
            if (popup.closed) {
                done(null); 
            }
        }, 500);

        const onMessage = (event) => {
            if (event.origin !== APP_ORIGIN || event.source !== popup) {
                return;
            }

            const data = event.data;
            if (data?.type !== 'scp-reauth-callback' || data.state !== state) {
                return;
            }

            done(data.error ? null : data.code);
        };

        const done = (value) => {
            clearInterval(poll);
            window.removeEventListener('message', onMessage);
            if (!popup.closed) {
                popup.close();
            }
            resolve(value);
        };

        window.addEventListener('message', onMessage);
    });
    if (!code) return false;

    const tokenResponse = await getTokenByAuthorizationCode(code, pkce.verifier);
    if (!tokenResponse.ok) return false;

    // Guard against re-authenticating as a different account in the popup login form.
    const tokens = await tokenResponse.json().catch(() => null);
    const sub = tokens?.id_token ? decodeJwtSub(tokens.id_token) : undefined;
    const matches = !!sub && sub === expectedSub;
    if (!matches && tokens?.id_token) {
        await endSsoSession(tokens.id_token);
    }
    return matches;
}

// Pin redirectUri explicitly, but preserve the deep-linked path/query so login
// returns the user to where they started. Without this, the adapter falls back to
// globalThis.location.href for any login() call it triggers internally
// (e.g. from #processInit when the current URL is an OIDC callback whose leftover
// query params it didn't recognize/strip) — sending Keycloak a redirect_uri that
// itself contains code/session_state/iss, which it rejects. Stripping just those
// leftover params (instead of discarding the whole URL) keeps deep links working.
const OIDC_PARAMS = ['code', 'state', 'session_state', 'iss', 'error', 'error_description'];

// The default response_mode is fragment, so right after login the OIDC params sit in the
// hash (#state=…&session_state=…&code=…). Keycloak rejects those in redirect_uri and
// post_logout_redirect_uri. A hash without them (e.g. an API docs anchor) is left untouched.
const stripOidcParamsFromHash = (url) => {
    const fragment = new URLSearchParams(url.hash.substring(1));
    if (!OIDC_PARAMS.some((param) => fragment.has(param))) return;
    OIDC_PARAMS.forEach((param) => fragment.delete(param));
    url.hash = fragment.toString();
};

const buildRedirectUri = () => {
    const url = new URL(window.location.href);
    OIDC_PARAMS.forEach((param) => url.searchParams.delete(param));
    stripOidcParamsFromHash(url);
    return url.toString();
};

let initOptions = {
    onLoad: 'login-required',
    checkLoginIframe: false,
    redirectUri: buildRedirectUri(),
};
const authenticated = await keycloak
    .init(initOptions)
    .catch(() => keycloak.login());

if (!authenticated) {
    await keycloak.login();
}

await keycloak.updateToken(-1).catch(() => keycloak.login());
